import { Alert, Box, Button, Container, ContentLayout, FormField, Header, Input, SpaceBetween } from '@cloudscape-design/components';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { useAuth } from '../contexts/AuthContext';

/**
 * Agent Login Page (Cloudscape Design System)
 * 
 * Internal agent login UI with NodeGeeks branding.
 * Handles both direct access and OAuth authorization flow.
 * When accessed directly (no client_id), shows a simple login form.
 * When accessed via OAuth flow, validates parameters and proceeds with authorization.
 *
 * Requirements: 2.1, 2.2
 */
export const AgentLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, login, isLoading: authLoading, requiresProfileSelection, requiresTenantSelection } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // OAuth parameters
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const responseType = searchParams.get('response_type');
  const state = searchParams.get('state');
  const scope = searchParams.get('scope');
  
  // Check if this is an OAuth flow
  const isOAuthFlow = Boolean(clientId);
  
  // If already authenticated and in OAuth flow, redirect to authorize
  useEffect(() => {
    if (isAuthenticated && isOAuthFlow && !requiresProfileSelection && !requiresTenantSelection) {
      const params = new URLSearchParams(searchParams);
      navigate(`/oauth/authorize?${params.toString()}`);
    }
  }, [isAuthenticated, isOAuthFlow, requiresProfileSelection, requiresTenantSelection, navigate, searchParams]);

  // If profile selection is required, redirect to profile selection page
  useEffect(() => {
    if (isAuthenticated && requiresProfileSelection) {
      const params = isOAuthFlow ? `?${new URLSearchParams(searchParams).toString()}` : '';
      navigate(`/select-profile${params}`);
    }
  }, [isAuthenticated, requiresProfileSelection, isOAuthFlow, navigate, searchParams]);

  // If tenant selection is required, redirect to company selector page
  useEffect(() => {
    if (isAuthenticated && requiresTenantSelection && !requiresProfileSelection) {
      const params = isOAuthFlow ? `?${new URLSearchParams(searchParams).toString()}` : '';
      navigate(`/select-company${params}`);
    }
  }, [isAuthenticated, requiresTenantSelection, requiresProfileSelection, isOAuthFlow, navigate, searchParams]);

  // If authenticated with no profile/tenant selection needed and not OAuth, go to account
  useEffect(() => {
    if (isAuthenticated && !isOAuthFlow && !requiresProfileSelection && !requiresTenantSelection) {
      navigate('/account');
    }
  }, [isAuthenticated, isOAuthFlow, requiresProfileSelection, requiresTenantSelection, navigate]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    
    try {
      await login(email, password);
      // Navigation is handled by useEffect hooks based on auth state
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Show loading while checking auth state
  if (authLoading) {
    return (
      <AuthLayout>
        <Box textAlign="center" padding="xl">
          Loading...
        </Box>
      </AuthLayout>
    );
  }
  
  return (
    <AuthLayout>
      <ContentLayout
        header={
          <Header
            variant="h1"
            description={
              isOAuthFlow 
                ? 'Sign in to authorize the application'
                : 'Sign in to NodeGeeks Auth Portal'
            }
          >
            Sign In
          </Header>
        }
      >
        <Container>
          <form onSubmit={handleSubmit}>
            <SpaceBetween size="l">
              {error && (
                <Alert type="error" dismissible onDismiss={() => setError(null)}>
                  {error}
                </Alert>
              )}
              
              {!isOAuthFlow && (
                <Alert type="info">
                  This is the NodeGeeks Authentication Portal. To sign in to an application, 
                  please access it through the application's login page.
                </Alert>
              )}
              
              {isOAuthFlow && clientId && (
                <Alert type="info">
                  You are signing in to authorize access for application: <strong>{clientId}</strong>
                </Alert>
              )}
              
              <FormField label="Email Address">
                <Input
                  type="email"
                  value={email}
                  onChange={({ detail }) => setEmail(detail.value)}
                  placeholder="Enter your email"
                  disabled={isLoading}
                  autoComplete="email"
                />
              </FormField>
              
              <FormField label="Password">
                <Input
                  type="password"
                  value={password}
                  onChange={({ detail }) => setPassword(detail.value)}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </FormField>
              
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="primary"
                  loading={isLoading}
                  formAction="submit"
                >
                  Sign In
                </Button>
                
                <Button
                  variant="link"
                  onClick={() => navigate('/forgot-password')}
                  disabled={isLoading}
                >
                  Forgot Password?
                </Button>
              </SpaceBetween>
              
              {/* Note: Registration is handled by Operations Dashboard, not here */}
            </SpaceBetween>
          </form>
        </Container>
      </ContentLayout>
    </AuthLayout>
  );
};

// Default export for lazy loading
export default AgentLoginPage;
